import React, { useContext, useEffect, useRef, useState } from 'react'
import CreatePanel from '@/components/create/CreatePanel'
import MintPanel from '@/components/mint/MintPanel'
import AuctionPanel from '@/components/auction/AuctionPanel'
import MarketPanel from '@/components/market/MarketPanel'
import VotesPanel from '@/components/votes/VotesPanel'
import ClaimsPanel from '@/components/claims/ClaimsPanel'
import PortfolioPanel from '@/components/portfolio/PortfolioPanel'
import WalletsPanel from '@/components/wallets/WalletsPanel'
import StatsPanel from '@/components/stats/StatsPanel'
import HelpPanel from '@/components/help/HelpPanel'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import AirdropPanel from '@/components/airdrop/AirdropPanel'
import CrazyPanel from '@/components/crazy/CrazyPanel'
import { checkHashAndOpenTab } from '@/utils/hash'
import { Context } from '@/components/Context'
import { ArtData } from '@/database/art'
import Divider from '@/components/Divider'
import LoadImageButtonOnDemand from '@/components/LoadImageButtonOnDemand'
import Box from '@/components/boxes/Box'

const TABS = 12

const MemoizedAirdropPanel = React.memo(AirdropPanel)
const MemoizedCreatePanel = React.memo(CreatePanel)
const MemoizedMintPanel = React.memo(MintPanel)
const MemoizedAuctionPanel = React.memo(AuctionPanel)
const MemoizedMarketPanel = React.memo(MarketPanel)
const MemoizedVotesPanel = React.memo(VotesPanel)
const MemoizedClaimsPanel = React.memo(ClaimsPanel)
const MemoizedWalletsPanel = React.memo(WalletsPanel)
const MemoizedPortfolioPanel = React.memo(PortfolioPanel)
const MemoizedStatsPanel = React.memo(StatsPanel)
const MemoizedHelpPanel = React.memo(HelpPanel)
const MemoizedCrazyPanel = React.memo(CrazyPanel)

const calculateSlidesPerView = () => {
    const screenWidth = window.innerWidth
    if (screenWidth >= 1080) return TABS
    // Adjust the scaling logic below as needed
    return Math.max(1, Math.floor(screenWidth / 90)) // Example scaling logic
}

const setHashOnIndex = (index: number) => {
    switch (index) {
        case 0:
            window.location.hash = `#airdrop`
            break
        case 1:
            window.location.hash = `#create`
            break
        case 2:
            window.location.hash = `#mint`
            break
        case 3:
            window.location.hash = `#auction`
            break
        case 4:
            window.location.hash = `#market`
            break
        case 5:
            window.location.hash = `#votes`
            break
        case 6:
            window.location.hash = `#claims`
            break
        case 7:
            window.location.hash = `#portfolio`
            break
        case 8:
            window.location.hash = `#wallets`
            break
        case 9:
            window.location.hash = `#dex`
            break
        case 10:
            window.location.hash = `#help`
            break
        case 11:
            window.location.hash = `#stats`
            break
        default:
            window.location.hash = ``
            break
    }
}

const NavigationTabs = () => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [slidesPerView, setSlidesPerView] = useState(TABS)
    const { artDataList, proposalData } = useContext(Context)
    const [showRoom, setShowRoom] = useState({} as ArtData)
    const swiperRef = useRef(null)

    useEffect(() => {
        // @ts-expect-error swiper
        swiperRef.current?.swiper.slideTo(activeIndex)
    }, [activeIndex])

    useEffect(() => {
        const handleResize = () => {
            const slidesPerViewNeeded = calculateSlidesPerView()
            setSlidesPerView(slidesPerViewNeeded)
        }

        // Check hash on mount
        checkHashAndOpenTab(setActiveIndex, artDataList, proposalData, setShowRoom)
        handleResize()

        // Add event listener for hash changes
        window.addEventListener(
            'hashchange',
            () => checkHashAndOpenTab(setActiveIndex, artDataList, proposalData, setShowRoom),
            false,
        )
        window.addEventListener('resize', handleResize, false)

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener(
                'hashchange',
                () => checkHashAndOpenTab(setActiveIndex, artDataList, proposalData, setShowRoom),
                false,
            )
            window.removeEventListener('resize', handleResize, false)
        }
    }, [artDataList])

    return !showRoom.art ? (
        <div>
            <Tabs defaultFocus={false} focusTabOnClick={true} selectedIndex={activeIndex ?? 0} onSelect={() => {}}>
                <Swiper
                    ref={swiperRef}
                    slidesPerView={slidesPerView}
                    spaceBetween={0}
                    freeMode={true}
                    centeredSlides={true}
                    centeredSlidesBounds={true}
                    onClick={(index) => {
                        setHashOnIndex(index.clickedIndex)
                        setActiveIndex(index.clickedIndex)
                    }}
                    modules={[FreeMode, Navigation]}
                    navigation={slidesPerView < TABS}
                >
                    <TabList>
                        <SwiperSlide className="no-border">
                            <Tab>Airdrop</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Your&nbsp;Art</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Mint</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Auction</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Market</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Your&nbsp;Vote</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Claims</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Wallets</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Portfolio</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>CRAZY</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Help</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Stats</Tab>
                        </SwiperSlide>
                    </TabList>
                </Swiper>
                <div>
                    <TabPanel>
                        <MemoizedAirdropPanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedCreatePanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedMintPanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedAuctionPanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedMarketPanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedVotesPanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedClaimsPanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedWalletsPanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedPortfolioPanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedCrazyPanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedHelpPanel />
                    </TabPanel>
                    <TabPanel>
                        <MemoizedStatsPanel />
                    </TabPanel>
                </div>
            </Tabs>
        </div>
    ) : (
        <div>
            <Divider />
            <div className="buttons-container">
                <div className="artwork-box">
                    <div className="header-container menu-wrapper-column bottom">
                        <Box
                            caption={showRoom.tokenId ? 'NFT' : 'ART'}
                            text={'#' + (showRoom.tokenId ? showRoom.tokenId : showRoom.art.id)}
                        />
                        <Box caption={'Title'} text={showRoom.art.title} />
                    </div>
                </div>
            </div>
            <LoadImageButtonOnDemand artData={showRoom} maxWidth={true} />
        </div>
    )
}

export default React.memo(NavigationTabs)
